import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ReceiptRussianRuble, Clock, ExternalLink, Download, Loader2 } from 'lucide-react';
import { useCourierPayments, PaymentInfo } from '@/hooks/useCourierPayments';
import { formatDate } from '@/utils/dateUtils';
import { PaymentStatusBadge } from '@/pages/admin/payments/PaymentStatusBadge';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/sonner';

interface PaymentsCardProps {
  payments?: PaymentInfo[];
  totalPaid?: number;
  loading?: boolean;
  error?: string | null;
}

export function PaymentsCard({ payments: propPayments, totalPaid: propTotalPaid, loading: propLoading, error: propError }: PaymentsCardProps) {
  const navigate = useNavigate();
  const [loadingPaymentId, setLoadingPaymentId] = useState<string | null>(null);
  
  // Используем хук для загрузки данных ТОЛЬКО если они не были переданы через props
  const { 
    payments: hookPayments, 
    totalPaid: hookTotalPaid, 
    loading: hookLoading,
    error: hookError
  } = useCourierPayments();
  
  // Приоритет данным из props, если их нет - берем из хука
  const payments = propPayments || hookPayments || [];
  
  // ИСПРАВЛЕНО: Вычисляем общую сумму только для успешных платежей (paid или completed)
  const calculatedTotalPaid = useMemo(() => {
    return payments
      .filter(p => p.status === 'paid' || p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0);
  }, [payments]);
  
  // Используем правильно вычисленную сумму или данные из props
  const totalPaid = propTotalPaid ?? calculatedTotalPaid ?? 0;
  
  const loading = propLoading ?? hookLoading ?? false;
  const error = propError ?? hookError ?? null;

  console.log('PaymentsCard - данные:', { 
    propsPayments: propPayments?.length, 
    hookPayments: hookPayments?.length,
    payments: payments.length, 
    totalPaid,
    calculatedTotalPaid
  });

  // Фильтруем авторизационные платежи из списка, т.к. они отображаются отдельно
  const filteredPayments = useMemo(() => {
    return payments.filter(payment => payment.status !== 'auth');
  }, [payments]);

  // Находим ожидающие платежи (pending) после последнего успешного платежа
  const pendingPaymentsAfterLastSuccess = useMemo(() => {
    if (!filteredPayments.length) return [];
    
    // Находим дату последнего успешного платежа
    const successfulPayments = filteredPayments.filter(p => p.status === 'paid' || p.status === 'completed');
    const lastSuccessfulPaymentDate = successfulPayments.length > 0 
      ? new Date(successfulPayments[0].date).getTime()
      : 0;
    
    // Находим все pending платежи после этой даты
    return filteredPayments.filter(p => 
      p.status === 'pending' && 
      new Date(p.date).getTime() >= lastSuccessfulPaymentDate
    );
  }, [filteredPayments]);

  // Создаем URL для возврата после оплаты
  const getReturnUrl = (payment: PaymentInfo) => {
    // Базовый URL для возврата - страница успешной оплаты
    let returnUrl = `${window.location.origin}/payment/auth/success?payment_id=${payment.id}`;
    
    // Добавляем параметр subscription_id если он есть
    if (payment.subscriptionId) {
      returnUrl += `&subscription_id=${payment.subscriptionId}`;
    }
    
    return returnUrl;
  };

  // Функция для генерации URL оплаты с параметром возврата в зависимости от способа оплаты
  const getPaymentUrl = (payment: PaymentInfo) => {
    if (!payment.human_number) return '';
    
    const returnUrl = getReturnUrl(payment);
    
    // Используем разные URL в зависимости от способа оплаты
    if (payment.method === 'card') {
      // Для карточных платежей используем YooMoney
      return `https://yoomoney.ru/checkout/payments/v2/contract?orderId=${payment.human_number}&returnUrl=${encodeURIComponent(returnUrl)}`;
    } else {
      // Для СБП и других способов оплаты используем Nova
      return `https://sp1-nova.ru/api/pay/?subdomain=tuktukrent.amocrm.ru&invoice_id=${payment.human_number}&return_url=${encodeURIComponent(returnUrl)}`;
    }
  };

  // Обработчик для перехода на страницу оплаты
  const handlePaymentRedirect = async (payment: PaymentInfo) => {
    try {
      // Устанавливаем состояние загрузки
      setLoadingPaymentId(payment.id);
      
      // Показываем уведомление перед переходом
      toast.info('Подготовка страницы оплаты...', {
        description: 'Загрузка страницы',
        duration: 3000
      });
      
      // Для платежей в ожидании с ссылкой receiptUrl
      if (payment.status === 'pending' && payment.receiptUrl) {
        window.location.href = payment.receiptUrl;
        return;
      }
      
      const paymentUrl = getPaymentUrl(payment);
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        toast.error('Не удалось сформировать ссылку на оплату');
      }
      
      setLoadingPaymentId(null);
    } catch (error) {
      toast.error('Ошибка при переходе на страницу оплаты');
      console.error('Ошибка при переходе на страницу оплаты:', error);
      setLoadingPaymentId(null);
    }
  };

  // Функция для открытия чека
  const openReceipt = (receiptUrl: string | undefined) => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    }
  };

  // Обработчик клика на "Просмотреть все платежи"
  const handleViewAllPayments = () => {
    navigate('/courier/payments');
  };

  // ИСПРАВЛЕНО: Функция для получения понятного текста типа оплаты
  const getPaymentMethodText = (method: string): string => {
    switch (method) {
      case 'card': return 'Банковская карта';
      case 'cash': return 'Наличные';
      case 'account': return 'Счет';
      case 'sbp': return 'СБП';
      default: return method;
    }
  };

  if (loading) {
    return (
      <Card className="w-full bg-card/80 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ReceiptRussianRuble className="h-5 w-5" /> 
            Загрузка платежей...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-6">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-muted-foreground">Загружаем данные о платежах...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full bg-card/80 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ReceiptRussianRuble className="h-5 w-5" /> 
            Оплаты и счета
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-destructive">
            <p>{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.location.reload()}
              className="mt-2"
            >
              Обновить страницу
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Если нет данных о платежах, показываем информационное сообщение
  if (payments.length === 0) {
    return (
      <Card className="w-full bg-card/80 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ReceiptRussianRuble className="h-5 w-5" /> 
            Оплаты и счета
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">У вас пока нет платежей</p>
            <p className="text-xs text-muted-foreground mt-1">История платежей появится здесь после оплаты заказа</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full bg-card/80 shadow-md">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ReceiptRussianRuble className="h-5 w-5" /> 
          Оплаты и счета
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Показываем блок с ожидающими оплатами, если они есть */}
        {pendingPaymentsAfterLastSuccess.length > 0 && (
          <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-700">
            <h3 className="text-sm font-semibold mb-2">Требуется оплата:</h3>
            <div className="space-y-2">
              {pendingPaymentsAfterLastSuccess.map(payment => (
                <div key={`pending-${payment.id}`} className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">{payment.amount.toLocaleString()} ₽</p>
                    <p className="text-xs text-muted-foreground">{formatDate(payment.date)}</p>
                    <PaymentStatusBadge status={payment.status} className="text-xs mt-1" />
                  </div>
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => handlePaymentRedirect(payment)}
                    className="whitespace-nowrap"
                    disabled={loadingPaymentId === payment.id}
                  >
                    {loadingPaymentId === payment.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Загрузка...
                      </>
                    ) : (
                      <>
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Оплатить сейчас
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
          <span className="font-medium">Общая оплаченная сумма:</span>
          <span className="text-lg font-bold">{totalPaid.toLocaleString()} ₽</span>
        </div>
        
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Последние платежи:</h3>
          
          <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
            {filteredPayments.slice(0, 5).map(payment => (
              <div key={payment.id} className="flex justify-between items-center p-2 border-b border-border/30">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {formatDate(payment.date)}
                    {payment.rentalHumanNumber && (
                      <span className="text-xs ml-1 text-muted-foreground">
                        ({payment.rentalHumanNumber})
                      </span>
                    )}
                  </span>
                  {/* ИСПРАВЛЕНО: Улучшенное отображение типа платежа */}
                  <span className="text-xs text-muted-foreground">{getPaymentMethodText(payment.method)}</span>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="font-medium">{payment.amount.toLocaleString()} ₽</span>
                  <PaymentStatusBadge status={payment.status} className="flex items-center text-xs" />
                </div>
                <div className="ml-2">
                  {(payment.status === 'paid' || payment.status === 'completed') && payment.receiptUrl && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="p-1 h-auto"
                      onClick={() => openReceipt(payment.receiptUrl)}
                      title="Открыть чек"
                    >
                      <Download className="h-4 w-4 text-primary" />
                    </Button>
                  )}
                  {(payment.status === 'pending') && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="p-1 h-auto"
                      onClick={() => handlePaymentRedirect(payment)}
                      disabled={loadingPaymentId === payment.id}
                      title="Оплатить"
                    >
                      {loadingPaymentId === payment.id ? (
                        <Loader2 className="h-4 w-4 text-primary animate-spin" />
                      ) : (
                        <ExternalLink className="h-4 w-4 text-primary" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={handleViewAllPayments}
        >
          Просмотреть все платежи
        </Button>
      </CardFooter>
    </Card>
  );
}
